/**
 * The one HTTP client for the back office: configuration, session storage, the error envelope,
 * transparent token refresh and every endpoint the console calls.
 */
import type { AppConfig as AppConfigDoc, ServerSettingsView, ApiError as ApiErrorEnvelope, ApiErrorCode, AuthTokens, DeviceInfo, EmailCodeRequest, EmailVerifyRequest, HEADERS, RefreshRequest } from '@lc/contracts';
import type { z } from 'zod';
import type {
  AdminRole,
  AiCost,
  Insights,
  InsightDays,
  AiCostDays,
  Growth,
  AuditFilter,
  AuditRow,
  BroadcastPreview,
  Dashboard,
  FailedRenderJob,
  JobDetail,
  JobSearch,
  Me,
  PersonalChapterRow,
  PlayerDetail,
  PlayerRow,
  PushBody,
  RangeDays,
  RenderDetail,
  RenderRow,
  ReportWithContext,
  Segment,
  StoryChapterDetail,
  StorySummary,
  StoryThread,
  TeamMember,
  ContentReport,
  CreditsBody,
  DecisionBody,
  FeatureFlag,
  FlagBody,
  Job,
  Metrics,
  Ok,
  ReportState,
  RetentionRow,
  SimulateBody,
  SimulationResult,
  TemplateSummary,
} from './types';

// ---------------------------------------------------------------------------
// configuration
// ---------------------------------------------------------------------------

/** Absolute URL, or a same-origin path such as `/api/v1` when using the dev proxy (vite.config.ts). */
export const API_BASE = (import.meta.env.VITE_API_URL || 'http://localhost:4000/api/v1').replace(/\/+$/, '');
const TIMEOUT_MS = 20_000;
/** Refresh a little before expiry so a request never leaves with a token about to lapse. */
const REFRESH_SKEW_MS = 30_000;
const REQUEST_ID_HEADER: (typeof HEADERS)['requestId'] = 'x-request-id';

// ---------------------------------------------------------------------------
// session (localStorage: survives closing the tab; the API ends it ADMIN_SESSION_HOURS after sign-in)
// ---------------------------------------------------------------------------

export interface Session {
  tokens: AuthTokens;
  email: string;
  /** Epoch ms of sign-in. */
  signedInAt: number;
}

const SESSION_KEY = 'lc.admin.session';
const DEVICE_KEY = 'lc.admin.device';

function readStorage(store: () => Storage, key: string): string | null {
  try {
    return store().getItem(key);
  } catch {
    return null;
  }
}
function writeStorage(store: () => Storage, key: string, value: string | null) {
  try {
    if (value === null) store().removeItem(key);
    else store().setItem(key, value);
  } catch {
    // storage blocked (private mode); the in-memory copy still works for this tab
  }
}

function loadSession(): Session | null {
  const raw = readStorage(() => localStorage, SESSION_KEY);
  if (!raw) return null;
  try {
    const s = JSON.parse(raw) as Session;
    return s?.tokens?.accessToken && s.tokens.refreshToken && typeof s.signedInAt === 'number' ? s : null;
  } catch {
    return null;
  }
}

let session: Session | null = loadSession();
const listeners = new Set<() => void>();
/** Why the last session ended, when it wasn't the user's own sign-out (shown on the sign-in screen). */
let endedReason: 'expired' | null = null;

export const sessionStore = {
  get: (): Session | null => session,
  endedReason: () => endedReason,
  set(next: Session | null) {
    if (next) endedReason = null;
    session = next;
    writeStorage(() => localStorage, SESSION_KEY, next ? JSON.stringify(next) : null);
    listeners.forEach((l) => l());
  },
  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};

/** A stable id for this browser; not a secret, so it may outlive the session. */
function deviceId(): string {
  const existing = readStorage(() => localStorage, DEVICE_KEY);
  if (existing && existing.length >= 8) return existing;
  const id = `admin-web-${crypto.randomUUID()}`;
  writeStorage(() => localStorage, DEVICE_KEY, id);
  return id;
}

// ---------------------------------------------------------------------------
// errors
// ---------------------------------------------------------------------------

export type ClientErrorCode = ApiErrorCode | 'network' | 'timeout';

/** Every failure surfaces as this type: the server envelope, a network loss or a timeout. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: ClientErrorCode,
    message: string,
    readonly requestId: string | null = null,
    readonly details: unknown = undefined,
  ) {
    super(message);
    this.name = 'ApiError';
  }
  get isForbidden() {
    return this.status === 403;
  }
  get isUnauthorized() {
    return this.status === 401;
  }
  /** 4xx other than rate limiting: retrying will not help. */
  get isClientError() {
    return this.status >= 400 && this.status < 500 && this.status !== 429;
  }
}

export const isApiError = (e: unknown): e is ApiError => e instanceof ApiError;

async function toApiError(res: Response): Promise<ApiError> {
  let body: Partial<ApiErrorEnvelope> | null = null;
  try {
    body = (await res.json()) as Partial<ApiErrorEnvelope>;
  } catch {
    // non-JSON (proxy page); fall back to the status line
  }
  const env = body?.error;
  const code: ClientErrorCode = env?.code ?? (res.status === 401 ? 'unauthorized' : res.status === 403 ? 'forbidden' : res.status === 404 ? 'not_found' : 'internal');
  return new ApiError(res.status, code, env?.message ?? (res.statusText || `HTTP ${res.status}`), env?.requestId ?? res.headers.get(REQUEST_ID_HEADER), env?.details);
}

// ---------------------------------------------------------------------------
// transport
// ---------------------------------------------------------------------------

type Query = Record<string, string | number | boolean | null | undefined>;

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT';
  body?: unknown;
  query?: Query;
  /** false for the sign-in endpoints. */
  auth?: boolean;
  signal?: AbortSignal | undefined;
}

function buildUrl(path: string, query?: Query): string {
  // API_BASE may be absolute (default) or same-origin relative (/api/v1 through the Vite proxy).
  const url = new URL(`${API_BASE}${path}`, window.location.origin);
  for (const [k, v] of Object.entries(query ?? {})) if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, String(v));
  return url.toString();
}

async function send(path: string, opts: RequestOptions, token: string | null): Promise<Response> {
  const headers: Record<string, string> = { accept: 'application/json' };
  if (opts.body !== undefined) headers['content-type'] = 'application/json';
  if (token) headers['authorization'] = `Bearer ${token}`;
  const timeout = AbortSignal.timeout(TIMEOUT_MS);
  const signal = opts.signal ? AbortSignal.any([opts.signal, timeout]) : timeout;
  try {
    return await fetch(buildUrl(path, opts.query), {
      method: opts.method ?? 'GET',
      headers,
      body: opts.body === undefined ? null : JSON.stringify(opts.body),
      signal,
      cache: 'no-store',
    });
  } catch (e) {
    if (timeout.aborted) throw new ApiError(0, 'timeout', 'The API did not answer in time.');
    if (opts.signal?.aborted) throw e; // caller cancelled (React Query); let it propagate as-is
    throw new ApiError(0, 'network', `Cannot reach the API at ${API_BASE}.`);
  }
}

async function parse<T>(res: Response): Promise<T> {
  if (!res.ok) throw await toApiError(res);
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

let refreshing: Promise<string> | null = null;

/** Single-flight refresh: concurrent 401s share one POST /auth/refresh. */
function refreshAccessToken(): Promise<string> {
  refreshing ??= (async () => {
    const current = sessionStore.get();
    if (!current) throw new ApiError(401, 'unauthorized', 'Signed out.');
    const body: z.infer<typeof RefreshRequest> = { refreshToken: current.tokens.refreshToken };
    const res = await send('/auth/refresh', { method: 'POST', body }, null);
    if (!res.ok) {
      const err = await toApiError(res);
      // A rejected refresh token ends the session; a 5xx keeps it so the next call can try again.
      if (err.isClientError) sessionStore.set(null);
      throw err;
    }
    const tokens = (await res.json()) as AuthTokens;
    // Only keep the new tokens if nobody signed out while we were waiting.
    if (sessionStore.get()?.tokens.refreshToken === current.tokens.refreshToken) sessionStore.set({ ...current, tokens });
    return tokens.accessToken;
  })().finally(() => {
    refreshing = null;
  });
  return refreshing;
}

async function accessToken(): Promise<string> {
  const s = sessionStore.get();
  if (!s) throw new ApiError(401, 'unauthorized', 'Signed out.');
  const expiresAt = Date.parse(s.tokens.accessTokenExpiresAt);
  if (Number.isFinite(expiresAt) && expiresAt - Date.now() < REFRESH_SKEW_MS) return refreshAccessToken();
  return s.tokens.accessToken;
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  if (opts.auth === false) return parse<T>(await send(path, opts, null));
  let res = await send(path, opts, await accessToken());
  if (res.status === 401) {
    res = await send(path, opts, await refreshAccessToken());
    // Still rejected with a fresh token: the session is gone server-side (or older than the console allows).
    if (res.status === 401) {
      endedReason = 'expired';
      sessionStore.set(null);
    }
  }
  return parse<T>(res);
}

// ---------------------------------------------------------------------------
// endpoints
// ---------------------------------------------------------------------------

type EmailCodeBody = z.infer<typeof EmailCodeRequest>;
type EmailVerifyBody = z.infer<typeof EmailVerifyRequest>;

const device = (): DeviceInfo => ({ deviceId: deviceId(), platform: 'web', appVersion: 'admin' });
const id = encodeURIComponent;

export const api = {
  auth: {
    requestCode: (email: string) => request<void>('/auth/email/request', { method: 'POST', auth: false, body: { email } satisfies EmailCodeBody }),
    async verifyCode(email: string, code: string): Promise<Session> {
      const body: EmailVerifyBody = { email, code, device: device() };
      const tokens = await request<AuthTokens>('/auth/email/verify', { method: 'POST', auth: false, body });
      const next = { tokens, email, signedInAt: Date.now() };
      sessionStore.set(next);
      return next;
    },
    async signOut(): Promise<void> {
      try {
        if (sessionStore.get()) await request<void>('/auth/logout', { method: 'POST' });
      } catch {
        // best effort: the local session is dropped either way
      } finally {
        sessionStore.set(null);
      }
    },
  },

  metrics: (signal?: AbortSignal) => request<Metrics>('/admin/metrics', { signal }),
  retention: (signal?: AbortSignal) => request<RetentionRow[]>('/admin/retention', { signal }),

  decideReport: (reportId: string, body: DecisionBody) => request<Ok>(`/admin/reports/${id(reportId)}/decision`, { method: 'POST', body }),

  grantCredits: (userId: string, body: CreditsBody) => request<Ok>(`/admin/users/${id(userId)}/credits`, { method: 'POST', body }),

  retryJob: (jobId: string, reason: string) => request<Ok>(`/admin/jobs/${id(jobId)}/retry`, { method: 'POST', body: { reason } }),

  content: (signal?: AbortSignal) => request<ContentReport>('/admin/content', { signal }),
  templates: (signal?: AbortSignal) => request<TemplateSummary[]>('/admin/content/templates', { signal }),
  simulate: (key: string, body: SimulateBody) => request<SimulationResult>(`/admin/content/templates/${id(key)}/simulate`, { method: 'POST', body }),

  appConfig: (signal?: AbortSignal) => request<AppConfigDoc>('/admin/app-config', { signal }),
  saveAppConfig: (body: { config: Record<string, unknown>; note: string }) => request<AppConfigDoc>('/admin/app-config', { method: 'PUT', body }),
  settings: (signal?: AbortSignal) => request<ServerSettingsView>('/admin/settings', { signal }),
  saveSettings: (body: { config: Record<string, unknown>; note: string }) => request<ServerSettingsView>('/admin/settings', { method: 'PUT', body }),

  flags: (signal?: AbortSignal) => request<FeatureFlag[]>('/admin/flags', { signal }),
  setFlag: (key: string, body: FlagBody) => request<Ok>(`/admin/flags/${id(key)}`, { method: 'PUT', body }),


  // ---------- operator console ----------

  me: (signal?: AbortSignal) => request<Me>('/admin/me', { signal }),
  team: (signal?: AbortSignal) => request<TeamMember[]>('/admin/team', { signal }),
  setRoles: (body: { email: string; roles: AdminRole[]; reason: string }) => request<TeamMember>('/admin/team', { method: 'PUT', body }),
  dashboard: (range: RangeDays, signal?: AbortSignal) => request<Dashboard>('/admin/dashboard', { query: { range }, signal }),
  aiCost: (days: AiCostDays, signal?: AbortSignal) => request<AiCost>('/admin/ai-cost', { query: { days }, signal }),
  insights: (days: InsightDays, signal?: AbortSignal) => request<Insights>('/admin/insights', { query: { days }, signal }),
  growth: (signal?: AbortSignal) => request<Growth>('/admin/growth', { signal }),
  launchEmail: (reason: string) => request<{ ok: true; recipients: number }>('/admin/growth/launch-email', { method: 'POST', body: { reason } }),

  players: (q: string, signal?: AbortSignal) => request<PlayerRow[]>('/admin/players', { query: { q }, signal }),
  player: (userId: string, signal?: AbortSignal) => request<PlayerDetail>(`/admin/players/${id(userId)}`, { signal }),
  playerAction: (userId: string, action: PlayerAction, body: Record<string, unknown>) => request<Record<string, unknown>>(`/admin/players/${id(userId)}/${PLAYER_ACTION_PATH[action]}`, { method: 'POST', body }),

  storyChapters: (f: { status?: string | undefined; issues?: boolean | undefined; userId?: string | undefined; npcId?: string | undefined }, before: string | undefined, signal?: AbortSignal) =>
    request<PersonalChapterRow[]>('/admin/story/chapters', { query: { ...f, before }, signal }),
  storyChapter: (chapterId: string, signal?: AbortSignal) => request<StoryChapterDetail>(`/admin/story/chapters/${id(chapterId)}`, { signal }),
  retryStory: (chapterId: string, reason: string) => request<{ ok: true; jobId: string }>(`/admin/story/chapters/${id(chapterId)}/retry`, { method: 'POST', body: { reason } }),
  storySummary: (signal?: AbortSignal) => request<StorySummary>('/admin/story/summary', { signal }),
  threads: (f: { status?: string | undefined; userId?: string | undefined }, signal?: AbortSignal) => request<StoryThread[]>('/admin/story/threads', { query: f, signal }),

  renders: (f: { tier?: string | undefined; partial?: boolean | undefined }, before: string | undefined, signal?: AbortSignal) =>
    request<{ renders: RenderRow[]; failedJobs: FailedRenderJob[]; thumbs: Record<string, string> }>('/admin/manga/renders', { query: { ...f, before }, signal }),
  render: (renderId: string, signal?: AbortSignal) => request<RenderDetail>(`/admin/manga/renders/${id(renderId)}`, { signal }),
  rerender: (chapterId: string, body: { reuseDrawnPages: boolean; reason: string }) => request<{ jobId: string; renderVersion: number }>(`/admin/manga/chapters/${id(chapterId)}/rerender`, { method: 'POST', body }),

  jobSearch: (f: JobSearch, before: string | undefined, signal?: AbortSignal) => request<Job[]>('/admin/jobs/search', { query: { ...f, before }, signal }),
  job: (jobId: string, signal?: AbortSignal) => request<JobDetail>(`/admin/jobs/${id(jobId)}`, { signal }),

  reportsWithContext: (state: ReportState, reason: string | undefined, signal?: AbortSignal) => request<ReportWithContext[]>('/admin/moderation/reports', { query: { state, reason }, signal }),

  broadcastPreview: (segment: Segment, signal?: AbortSignal) => request<BroadcastPreview>('/admin/notify/preview', { query: { segment }, signal }),
  broadcast: (body: PushBody & { segment: Segment }) => request<{ ok: true; recipients: number }>('/admin/notify/broadcast', { method: 'POST', body }),

  auditSearch: (f: AuditFilter, before: string | undefined, signal?: AbortSignal) => request<AuditRow[]>('/admin/audit/search', { query: { ...f, before }, signal }),
};

export type PlayerAction = 'status' | 'coins' | 'membership' | 'revokeMembership' | 'resetDaily' | 'clearPaymentHold' | 'export' | 'delete' | 'notify';
const PLAYER_ACTION_PATH: Record<PlayerAction, string> = {
  status: 'status',
  coins: 'coins',
  membership: 'membership',
  revokeMembership: 'membership/revoke',
  resetDaily: 'reset-daily',
  clearPaymentHold: 'payment-hold/clear',
  export: 'export',
  delete: 'delete',
  notify: 'notify',
};
